import {
  Build,
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  Host,
  Method,
  Prop,
  State,
  Watch,
  h,
  VNode,
  Listen,
} from '@stencil/core';

import { getActiveElement } from '../../utils/active-element';
import { closestElement } from '../../utils/dom';
import { createColorClasses } from '../../utils/theme';
import { debounce, debounceEvent } from '../../utils/throttle';
import { FormControl, FormControlWrap } from '../form-control/form-control';
import { OptionInterface } from '../option/option-interface';
import type { Dropdown } from '../dropdown/dropdown';
import type {
  Color,
  SelectChangeEventDetail,
  InputChangeEventDetail,
  ControlValidityEventDetail,
  ControlValidity,
} from '../../interface';

let selectIds = 0;

/**
 * The select component is a wrapper to the HTML select element with custom styling and additional functionality.
 * It accepts most of the same properties as the HTML [select](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select).
 * It's multi selection functionality is vastly improved from the native solution. It allows for the control of
 * value order through drag and drop or changing the insertion point with arrow keys. It also can allow for user defined values.
 *
 * @slot start - suitable for inline action buttons or icons that may add extra contextual information
 * @slot end - suitable for inline action buttons or icons that may add extra contextual information
 * @slot value-start - similar to 'start', however clicking elements using this slot will activate the input control. This would be suitable to show a secodary 'value' for context or to show a dropdown arrow if using a datalist
 * @slot value-end - similar to 'end', however clicking elements using this slot will activate the input control. This would be suitable to show a secodary 'value' for context or to show a dropdown arrow if using a datalist
 * @slot label - if you do not set a label attribute, you can use this slot for more complex markup
 * @slot helper - helper text to accompany the form field underneath.
 * @slot helper-end - helper text to accompany the form field on the side.
 * @slot down-arrow - use this to replace the default down arrow
 * @slot - default slot; nest `nano-option` elements
 */
@Component({
  tag: 'nano-select',
  styleUrl: 'select.scss',
  scoped: true,
})
export class Select implements ComponentInterface {
  private nativeSelect?: HTMLSelectElement;
  private valueItems: Array<HTMLSpanElement> = [];
  private dragVal: string;
  private mo?: MutationObserver;
  private selectId = `nano-select-${selectIds++}`;
  private rtl: boolean = false;
  private onInit: boolean = true;
  private customError: string = '';

  private get inputCtrl() {
    return this._inputCtrl;
  }
  private set inputCtrl(input) {
    if (input === this._inputCtrl) return;
    this._inputCtrl = input;
    this.setDataListOpts();
  }
  private _inputCtrl?: HTMLInputElement;

  // we don't want these rendered eles decorated with @State
  // because that will cause re-renders. Use get/set to set datalist options
  private _selectWrap: HTMLElement;
  private get selectWrap() {
    return this._selectWrap;
  }
  private set selectWrap(ele: HTMLElement) {
    if (this._selectWrap === ele) return;
    this._selectWrap = ele;
    this.setDataListOpts();
  }

  private _datalist: HTMLNanoDatalistElement;
  private get datalist() {
    return this._datalist;
  }
  private set datalist(ele: HTMLNanoDatalistElement) {
    if (this._datalist === ele) return;
    this._datalist = ele;
    this.setDataListOpts();
  }

  constructor() {
    this.showInlineValidation = debounce(this.showInlineValidation, 50);
  }

  @State() currInsertIndex: number = -1;
  @State() showErrorMsg = false;
  @State() errorMessage: string = '';
  @State() hasFocus = false;
  @State() hasLabelSlot: boolean = false;
  @State() hasHelperSlot: boolean = false;
  @State() hasHelperEndSlot = false;
  @State() inputSearchVal: string = '';

  @Element() el!: HTMLNanoSelectElement;

  /**
   * This will be true when the control is in an invalid state.
   * Validity is determined by the `required` prop. Or if custom validity message is set.
   */
  @Prop({ reflect: true })
  get invalid() {
    return this._invalid;
  }
  @State() _invalid = false;

  /**
   * Current validation message - if any. @readonly
   */
  @Prop()
  get validityMessage() {
    if (!this.nativeSelect) return '';
    return this.nativeSelect.validationMessage;
  }

  /**
   * The color to use from your application's color palette.
   * Default options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop() color?: Color;

  /**
   * This Boolean attribute lets you specify that a form control should have select focus when the page loads.
   */
  @Prop() autofocus = false;

  /**
   * If `true`, the user cannot interact with the select.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * When should the field perform validation
   */
  @Prop({ mutable: true }) validateOn?: 'dirty' | 'submit' | 'submitThenDirty' =
    'submitThenDirty';

  /**
   * Whether to show validation errors underneath input
   */
  @Prop({ reflect: true }) showInlineError = true;

  /**
   * String to place within a label element. Alternatively use the 'label' slot
   */
  @Prop() label!: string;

  /**
   * Visually hide the label - but make it accessible.
   */
  @Prop() hideLabel?: boolean = false;

  /**
   * Enable floating label behaviour. Will disable placeholder if set.
   */
  @Prop() floatLabel: boolean = false;

  /**
   * If `true`, the user can enter more than one value. This attribute applies when the type attribute is set to `"email"` or `"file"`, otherwise it is ignored.
   */
  @Prop() multiple?: boolean = false;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name: string = this.selectId + '-name';

  /**
   * Instructional text that shows before the select has a value. Text set here will be placed inside the label when a value is set. If you don't want this behaviour just set the first option to have no value.
   */
  @Prop() placeholder?: string | null;

  /**
   * If `true`, the user cannot modify the value.
   */
  @Prop({ reflect: true }) readonly = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required = false;

  /**
   * The initial size of the control.
   */
  @Prop() size?: number;

  /**
   * Maximum number of options that can be selected when multiple is true
   */
  @Prop() max?: number;

  /**
   * Minimum number of options that can be selected when multiple is true
   */
  @Prop() min?: number;

  /**
   * The form element to associate the select with (its form owner). Must be the id of a form.
   */
  @Prop() form?: string | null;

  /**
   * in multiple mode, allow users to enter their own values
   */
  @Prop() allowCustomValues: boolean = false;

  /**
   * The value of the select.
   */
  @Prop()
  get value() {
    return this._value;
  }
  set value(val: string | string[]) {
    if (typeof val === 'string') {
      if (this.multiple) this._value = val.split(',');
      else this._value = val;
    } else if (Array.isArray(val)) {
      if (this.multiple) this._value = val;
      else this._value = val[0];
    }
  }
  private _value: Array<string> | string = this.multiple ? [] : '';

  /**
   * You can set options via js as an array of strings: `domElement.options = ['option 1', 'option 2']` or
   * objects `domElement.options = [{label: 'option 1', value: 'opt-1'}]`. See `nano-option` docs for all available properties.
   * Alternatively, you can nest `<nano-option>` elements within a `<nano-select>`
   * Regardless - reading `options` will return the current component options - slotted or otherwise
   */
  @Prop()
  get options(): Array<OptionInterface | string> {
    if (this._options.length) return this._options;

    if (!this.datalist || !this.datalist.activeOptions) return [];

    return this._eOptions.map((ao) => {
      const { value, selected, label, filterMeta } = ao;
      return { value, selected, label, filterMeta };
    });
  }
  set options(opts: Array<OptionInterface | string>) {
    this._options = opts.map((opt) => {
      if (typeof opt === 'string') return { value: opt, label: opt };
      else return opt;
    });
  }
  private _options: OptionInterface[] = [];
  @State() _eOptions: OptionInterface[] = [];

  /**
   * If `true`, a clear icon will appear in the select when there is a value. Clicking it clears the select.
   */
  @Prop() clearSelect = false;

  /**
   * Hide the native select element and cover with a value 'mask'.
   * Enables a fluid nano-select width upon value change (if required). Doesn't work with multiple="true".
   */
  @Prop() mask = false;

  /**
   * Set the amount of time, in milliseconds, to wait to trigger the `nanoSearchChange` event after each keystroke.
   */
  @Prop() debounce = 0;

  @Watch('debounce')
  protected debounceChanged() {
    this.nanoSearchChange = debounceEvent(this.nanoSearchChange, this.debounce);
  }

  @Watch('required')
  @Watch('disabled')
  @Watch('min')
  @Watch('max')
  @Watch('readonly')
  protected shouldValidate() {
    if (this.onInit) return;
    if (this.invalid) this.showErrorMsg = this._invalid = false;

    this.customValidate();
    if (this.validateOn !== 'dirty') return;
    this.showInlineValidation();
  }

  /**
   * Update the native select element when the value changes
   */
  // eslint-disable-next-line @stencil/no-unused-watch
  @Watch('value')
  valueChanged() {
    this.customValidate();

    if (this.onInit) {
      if (this.multiple) this.currInsertIndex = this.value.length - 1;
      return;
    }
    requestAnimationFrame(() => {
      this.shouldValidate();
      this.nanoChange.emit({ value: this.value });
    });
  }

  /** nano-dropdown config options you can pass to the nested dropdown component */
  @Prop({ mutable: true }) dropDownConfig?: Partial<Dropdown> = {};

  @Watch('dropDownConfig')
  setDataListOpts() {
    if (!this.datalist || !this.selectWrap) return;

    this.datalist.input = this.inputCtrl;

    const currDWConfig = this.datalist.dropDownConfig || {};
    this.datalist.dropDownConfig = {
      ...currDWConfig,
      ...this.dropDownConfig,
      tetherTo: this.selectWrap,
    };
  }

  // Custom Events

  /**
   * Emitted when the value has changed.
   */
  @Event() nanoChange!: EventEmitter<SelectChangeEventDetail>;

  /**
   * Emitted when the select loses focus.
   */
  @Event() nanoBlur!: EventEmitter<void>;

  /**
   * Emitted when the select has focus.
   */
  @Event() nanoFocus!: EventEmitter<void>;

  /**
   * Emitted when the select has been created.
   * @internal
   */
  @Event() nanoDidLoad!: EventEmitter<void>;

  /**
   * Emitted when the select has been removed.
   * @internal
   */
  @Event() nanoDidUnload!: EventEmitter<void>;

  /**
   * Emitted when a keyboard input occurred on a multiple="true" input
   */
  @Event() nanoSearchChange!: EventEmitter<InputChangeEventDetail>;

  /**
   * Called when validation is performed (which depends on `validateOn`).
   * @returns event.detail: `{ isValid: boolean, errorMessage: string, originalEvent: Event }`.
   */
  @Event() nanoValidate: EventEmitter<ControlValidityEventDetail>;

  // Public Methods

  /**
   * Get the current state of the control.
   * @param validateFirst - perform validation first before reporting
   * @returns `{ isValid: boolean, errorMessage: string }`
   */
  @Method()
  async reportValidity(validateFirst: boolean): Promise<ControlValidity> {
    return new Promise((resolve) => {
      if (validateFirst) {
        this.customValidate();
        this.showInlineValidation();
      }
      setTimeout(() => {
        resolve({
          isValid: !this._invalid,
          errorMessage: this.nativeSelect.validationMessage,
        });
      }, 50);
    });
  }

  /**
   * Sets focus on the specified `nano-select`. Use this method instead of the global
   * `select.focus()`.
   */
  @Method()
  async setFocus() {
    if (!this.inputCtrl) return;
    this.inputCtrl.focus();
    setTimeout(() => this.inputCtrl.click(), 50);
  }

  /**
   * @returns the native `<select>` element used under the hood.
   */
  @Method()
  getSelectElement(): Promise<HTMLSelectElement> {
    return Promise.resolve(this.nativeSelect!);
  }

  /**
   * Invalidate the field and show a custom error message.
   * To clear the error you will need to re-call this method with an empty string.
   */
  @Method()
  async showError(message: string) {
    if (this.nativeSelect) {
      this.customError = message;
      this.nativeSelect.setCustomValidity(message);
      this.showInlineValidation();
    }
  }

  /* Logic */

  private get valArray() {
    return typeof this.value === 'string'
      ? this.value.length
        ? [this.value]
        : []
      : this.value;
  }

  private isValidValues() {
    return this.valArray.find((val) =>
      (this.options as OptionInterface[]).find((opt) => opt.value === val)
    );
  }

  private customValidate = () => {
    if (!this.nativeSelect || !!this.customError) return;

    this.nativeSelect.setCustomValidity('');
    // add custom validations 'cos html5 validations are a bit rubbish on selects
    if (this.required && !this.valArray.length) {
      this.nativeSelect.setCustomValidity('Please fill in this field.');
      return true;
    }
    if (
      this.valArray.length &&
      !this.allowCustomValues &&
      !this.isValidValues()
    ) {
      this.nativeSelect.setCustomValidity(
        'Please choose an item from this field.'
      );
      return true;
    }
    if (this.max && this.valArray.length > this.max) {
      this.nativeSelect.setCustomValidity(
        `Only up to ${this.max} values are allowed.`
      );
      return true;
    }
    if (this.min && this.valArray.length < this.min) {
      this.nativeSelect.setCustomValidity(
        `You must select a minimum of ${this.min} values.`
      );
      return true;
    }
    return false;
  };

  private showInlineValidation = (ev?: Event) => {
    if (this.validateOn === 'submitThenDirty') this.validateOn = 'dirty';

    this._invalid = false;
    this.showErrorMsg = false;
    this.errorMessage = '';

    if (!this.nativeSelect.validity.valid) {
      this.errorMessage = this.nativeSelect.validationMessage;
      this._invalid = true;
      this.showErrorMsg = true;
    }
    this.nanoValidate.emit({
      isValid: !this.invalid,
      errorMessage: this.errorMessage,
      originalEvent: ev,
    });
  };

  private handleInvalid = (ev: Event) => {
    this._invalid = true;

    if (this.validateOn === 'submit') {
      this._invalid = this.showErrorMsg = this.customValidate();
    }
    if (this.showInlineError) ev.preventDefault();

    requestAnimationFrame(() => this.showInlineValidation(ev));
  };

  private setValue = (e: CustomEvent<HTMLNanoOptionElement>) => {
    e.preventDefault();

    if (!this.multiple) {
      this.value = e.detail.value;
      return;
    }
    if (this.value && this.value.length && this.value.includes(e.detail.value))
      return;
    if (this.max && this.value.length === this.max) return;

    this.currInsertIndex++;
    this.value = [
      ...this.value.slice(0, this.currInsertIndex),
      e.detail.value,
      ...this.value.slice(this.currInsertIndex),
    ];
    this.inputSearchVal = '';
    // this.selectWrap.clientWidth; // force reflow
  };

  private removeValue = (toFind?: string) => {
    if (!this.multiple || !this.value.length) return;

    if (!toFind) toFind = this.value[this.value.length - 1];
    this.value = (this.value as Array<string>).filter((val) => val !== toFind);

    this.currInsertIndex--;
    // this.selectWrap.clientWidth; // force reflow
    this.setFocus();
  };

  private slotChangeObserver() {
    const mo = (this.mo = new MutationObserver(() =>
      this.processSlottedContent()
    ));
    mo.observe(this.el, { childList: true, subtree: true });
  }

  private processSlottedContent() {
    // see if we have label / helper content
    this.hasLabelSlot = !!this.el.querySelectorAll('[slot="label"]').length;
    this.hasHelperSlot = !!this.el.querySelector('[slot="helper"]');
    this.hasHelperEndSlot = !!this.el.querySelector('[slot="helper-end"]');
  }

  private getLabel(toFind: string) {
    const label = (this.options as OptionInterface[]).find((opt) => {
      return !opt.disabled && opt.value?.length && opt.value === toFind;
    });
    return label && label.label ? label.label : toFind;
  }

  /* Event handling */

  @Listen('reset', { target: 'body' })
  onReset(e) {
    const form = this.form
      ? document.querySelector('#' + this.form)
      : this.el.closest('form');
    if (!form || e.target !== this.el.closest('form')) return;

    this.clearSelectValue();
  }

  @Listen('mousedown', { target: 'body' })
  @Listen('keydown')
  handleBlur(e: KeyboardEvent | MouseEvent) {
    if (!this.hasFocus) return;

    const kev = e as KeyboardEvent;
    let target: Element;

    requestAnimationFrame(() => {
      if (kev.key) {
        if (kev.key !== 'Tab') return;
        target = getActiveElement();
      } else target = e.target as Element;

      if (closestElement(this.el.tagName.toLowerCase(), target) !== this.el) {
        this.onBlur();
      }
    });
  }

  private setOptions = () => {
    if (
      !this.datalist ||
      !this.datalist.activeOptions ||
      !this.datalist.activeOptions.length
    )
      return;

    this._eOptions = this.datalist.activeOptions.map((ao) => {
      const { value, selected, label, filterMeta } = ao;
      return { value, selected, label, filterMeta };
    });
    this.customValidate();
  };

  private handleDocumentKeyDown = (ev: KeyboardEvent) => {
    if (!this.multiple || !this.hasFocus) return;

    if (!this.inputSearchVal) {
      let rm: HTMLSpanElement;

      switch (ev.key) {
        case 'Backspace':
          if (this.inputCtrl.previousElementSibling)
            rm = this.inputCtrl.previousElementSibling as HTMLSpanElement;
          else break;

          this.removeValue(rm.dataset.value || null);
          break;

        case 'Delete':
          if (this.inputCtrl.nextElementSibling)
            rm = this.inputCtrl.nextElementSibling as HTMLSpanElement;
          else break;

          this.removeValue(rm.dataset.value || null);
          break;

        case 'ArrowLeft':
        case 'ArrowRight':
          if (!this.value.length) break;

          if (ev.key === 'ArrowLeft' && this.inputCtrl.previousSibling)
            this.currInsertIndex--;
          if (ev.key === 'ArrowRight' && this.inputCtrl.nextSibling)
            this.currInsertIndex++;

          setTimeout(() => {
            this.inputCtrl.focus();
          }, 20);
          ev.preventDefault();
          break;
      }
    }
  };

  private onBlur = () => {
    this.hasFocus = false;

    requestAnimationFrame(() => {
      if (this.validateOn === 'dirty' && !this.hasFocus)
        this.showInlineValidation();
    });
    this.nanoBlur.emit();

    if (!this.allowCustomValues && this.multiple) {
      this.inputSearchVal = '';
      const event = new window.Event('change');
      this.inputCtrl.dispatchEvent(event);
    }
  };

  private onFocus = () => {
    this.hasFocus = true;
    this.nanoFocus.emit();
  };

  private clearSelectValue = (ev?: Event) => {
    if (this.clearSelect && !this.readonly && !this.disabled && ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    if (this.multiple) {
      this.value = [];
      this.currInsertIndex = -1;
    } else this.value = '';

    this.inputCtrl.value = '';
    const event = new window.Event('change');
    this.inputCtrl.dispatchEvent(event);
  };

  private onClick = () => {
    this.setFocus();
  };

  /* Multi event handlers */

  private onDragStart = (ev: MouseEvent) => {
    const ele = ev.target as HTMLSpanElement;
    this.dragVal = ele.dataset.value;
  };

  private onDragEnd = (ev) => {
    this.dragVal = null;
    ev.preventDefault();
  };

  private onDragLeave = (ev: MouseEvent) => {
    const ele = ev.target as HTMLSpanElement;
    const spanVal: string =
      ele.dataset && ele.dataset.value ? ele.dataset.value : null;

    if (
      !spanVal ||
      this.dragVal === spanVal ||
      !this.value.includes(this.dragVal)
    ) {
      ev.preventDefault();
      return;
    }

    const to = this.value.indexOf(spanVal);
    const from = this.value.indexOf(this.dragVal);
    const tmpArr = this.value as Array<string>;
    const tmpVal = tmpArr[to];

    tmpArr[to] = tmpArr[from];
    tmpArr[from] = tmpVal;
    this.value = [...tmpArr];
  };

  private onMultiInput = (ev) => {
    this.inputSearchVal = ev.target.value.trim();
    this.nanoSearchChange.emit({ value: ev.target.value.trim() });
  };

  /* Stencil Component lifecycle hooks */

  connectedCallback() {
    this.debounceChanged();

    if (!Build.isBrowser) return;

    this.el.dispatchEvent(
      new CustomEvent('nanoDidLoad', {
        detail: this.el,
      })
    );
  }

  disconnectedCallback() {
    document.dispatchEvent(
      new CustomEvent('nanoDidUnload', {
        detail: this.el,
      })
    );

    if (!Build.isBrowser) return;
    if (this.mo) this.mo.disconnect();
  }

  componentDidLoad() {
    this.slotChangeObserver();
    this.customValidate();
    this.setDataListOpts();
    requestAnimationFrame(() => (this.onInit = false));
  }

  componentWillLoad() {
    this.processSlottedContent();
  }

  render() {
    const labelId = this.selectId + '-lbl';
    const moreId =
      this.showInlineError || this.hasHelperSlot
        ? this.selectId + '-moreId'
        : '';
    const helperEndId = this.hasHelperEndSlot ? this.selectId + '-helper' : '';

    this.rtl = (this.el.ownerDocument as Document).dir === 'rtl';
    this.valueItems = [];

    const compWrapOptions = (({
      el,
      floatLabel,
      label,
      errorMessage,
      showInlineError,
      hasHelperSlot,
      hasHelperEndSlot,
      hasLabelSlot,
      hideLabel,
      placeholder,
      rtl,
    }) => ({
      el,
      floatLabel,
      label,
      errorMessage,
      showInlineError,
      hasHelperSlot,
      hasHelperEndSlot,
      hasLabelSlot,
      hideLabel,
      placeholder,
      rtl,
    }))(this);
    const wrapOptions = {
      ...compWrapOptions,
      labelId,
      moreId,
      helperEndId,
      hasValue: !!this.value.length || !!this.inputSearchVal.length,
      controlId: this.selectId,
    };

    const controlOptions = (({ readonly, disabled }) => ({
      readonly,
      disabled,
      clearControl: this.clearSelect,
    }))(this);

    return (
      <Host
        type={this.multiple ? 'select-multiple' : 'select-one'}
        aria-disabled={this.disabled ? 'true' : null}
        class={{
          ...createColorClasses(this.color),
          'has-value': !!this.value.length || !!this.inputSearchVal.length,
          'has-focus': this.hasFocus,
          'is-invalid': this.invalid,
          'has-label': this.label !== null && !this.floatLabel,
          'has-float-label': this.label !== null && this.floatLabel,
          rtl: this.rtl,
          'has-multiple': this.multiple,
          'has-clr-btn': this.clearSelect,
          masked: this.mask,
        }}
      >
        <FormControlWrap {...wrapOptions}>
          <FormControl
            {...controlOptions}
            onClearText={this.clearSelectValue}
            control={this.el}
            ref={(el) => (this.selectWrap = el)}
            endValueSlot={
              <slot name="down-arrow">
                <nano-icon slot="value-end" name="light/chevron-down" />
              </slot>
            }
          >
            {this.multiple && (
              <div class="select__multi-wrap select">
                {this.multipleValues(labelId, moreId, helperEndId)}
              </div>
            )}
            {!this.multiple && [
              this.mask && (
                <div class="select__mask">
                  {this.getLabel(this.value as string)}
                </div>
              ),
              <input
                id={this.selectId}
                class="select__native-input"
                ref={(input) => (this.inputCtrl = input)}
                aria-labelledby={labelId + ' ' + moreId + ' ' + helperEndId}
                disabled={this.disabled}
                form={this.form}
                placeholder={this.placeholder}
                readOnly={true}
                required={this.required}
                value={this.getLabel(this.value as string)}
                onFocus={this.onFocus}
              />,
            ]}
          </FormControl>
        </FormControlWrap>
        {!this.readonly && !this.disabled && (
          <nano-datalist
            onNanoOptionsUpdated={this.setOptions}
            ref={(el) => (this.datalist = el)}
            selected={this.valArray}
            type={this.multiple ? 'selctMulti' : 'select'}
            onNanoSelect={this.setValue}
            onNanoDeselect={(e) => {
              e.preventDefault();
              this.removeValue(e.detail.value);
            }}
            options={this._options.length ? this._options : undefined}
          >
            {this.allowCustomValues && this.multiple && !!this.inputSearchVal && (
              <nano-option
                slot="list-top"
                value={this.inputSearchVal}
                selected={false}
                label={this.inputSearchVal}
                onNanoSelect={() => this.inputCtrl.focus()}
              >
                <span slot="check-icon"></span>
                Add '{this.inputSearchVal}'
              </nano-option>
            )}
            <slot />
          </nano-datalist>
        )}

        <select
          id={this.selectId + '-hidden'}
          class="select__native-ctrl"
          ref={(select) => (this.nativeSelect = select)}
          aria-labelledby={labelId + ' ' + moreId + ' ' + helperEndId}
          disabled={this.disabled}
          form={this.form}
          multiple={this.multiple}
          name={this.name}
          required={this.required}
          onInvalid={this.handleInvalid}
        >
          {this.allowCustomValues &&
            this.valArray.map((val) => {
              return (
                <option value={val} selected={true}>
                  {val}
                </option>
              );
            })}
          {!this.allowCustomValues &&
            this.options.map((opt: OptionInterface) => {
              return (
                <option
                  value={opt.value}
                  selected={this.valArray.includes(opt.value)}
                  disabled={opt.disabled}
                  label={opt.label}
                >
                  {this.valArray.includes(opt.value)}
                </option>
              );
            })}
        </select>
      </Host>
    );
  }

  private multipleValues(
    labelId: string,
    moreId: string,
    helperEndId: string
  ): VNode | (VNode | VNode[])[] {
    const input: VNode = (
      <input
        class="select__multi-input"
        id={this.selectId}
        ref={(input) => (this.inputCtrl = input)}
        readOnly={this.readonly}
        disabled={this.disabled}
        autoFocus={this.autofocus}
        autocomplete="off"
        onKeyDown={this.handleDocumentKeyDown}
        onInput={this.onMultiInput}
        value={this.inputSearchVal}
        onTouchStart={this.onClick}
        onMouseDown={this.onClick}
        onFocus={this.onFocus}
        placeholder={
          this.placeholder && !this.value.length ? this.placeholder : ''
        }
        aria-labelledby={labelId + ' ' + moreId + ' ' + helperEndId}
      />
    );
    if (!this.value.length) return input;

    return (this.value as Array<string>).map((val, i) => {
      let toReturn: VNode | VNode[] = (
        <span
          onDragStart={this.onDragStart}
          onDragLeave={this.onDragLeave}
          onDragEnd={this.onDragEnd}
          onDragOver={(e) => e.preventDefault()}
          draggable
          data-value={val}
          ref={(span) => this.valueItems.push(span)}
          class="select__multi-value"
        >
          <span>{this.getLabel(val)}</span>
          <button
            class="select__multi-value-remove"
            type="button"
            tabindex="-1"
            onTouchEnd={() => {
              this.removeValue(val);
            }}
            onMouseUp={() => {
              this.removeValue(val);
            }}
          >
            <nano-icon name="light/times"></nano-icon>
          </button>
        </span>
      );
      if (i === 0 && this.currInsertIndex < 0)
        toReturn = [input, toReturn as VNode];
      else if (i === this.currInsertIndex)
        toReturn = [toReturn as VNode, input];
      return toReturn;
    });
  }
}
